import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { VaultProvider, useVault } from './contexts/VaultContext';
import VaultPINSetup from './components/VaultPINSetup';
import VaultUnlock from './components/VaultUnlock';

// Determine which app mode we're running
// Set REACT_APP_MODE=standalone for myemergencycard.app
// Set REACT_APP_MODE=health for emergencycard.health (or leave empty)
const APP_MODE = process.env.REACT_APP_MODE || 'health';
const isStandaloneMode = APP_MODE === 'standalone';

// ============================================
// 10X SEO CONFIGURATION - UNIQUE PER APP
// ============================================
const SEO_CONFIG = {
  standalone: {
    title: 'Emergency Medical Card - QR Code Medical ID | Save Lives in Emergencies',
    description: 'Create your digital emergency medical card with QR code for just $1. Store vital health info, allergies, medications & emergency contacts. Works 100% offline. Scannable by any phone. Available in 20+ languages. Trusted by 50,000+ users worldwide.',
    keywords: 'emergency medical card, medical ID card, QR code medical alert, digital health card, emergency contact card, medical alert bracelet alternative, first responder medical info, medical emergency card, health QR code, emergency medical information card, medical ID bracelet alternative, digital medical ID, emergency health card, medical alert card',
    ogImage: 'https://myemergencycard.app/og-image.png',
    canonical: 'https://myemergencycard.app',
    siteName: 'My EmergencyCard',
    twitterHandle: '@myemergencycard',
    themeColor: '#dc2626',
    author: 'My EmergencyCard'
  },
  health: {
    title: 'EmergencyCard Health - Secure Family Health Vault | HIPAA Compliant Medical Records',
    description: 'Store your family\'s complete medical history with military-grade zero-knowledge encryption. Track prescriptions, lab results, vaccinations. Share instantly with doctors. HIPAA compliant. Trusted by 25,000+ families. Plans from $2.99/month.',
    keywords: 'family health records app, encrypted medical records, HIPAA compliant health app, secure health vault, prescription tracker, medical history storage, family health management, doctor record sharing, health diary app, vaccination tracker, lab results storage, family medical records, health passport app, medical records app, secure health records',
    ogImage: 'https://emergencycard.health/og-image-health.png',
    canonical: 'https://emergencycard.health',
    siteName: 'EmergencyCard Health',
    twitterHandle: '@emergencycard',
    themeColor: '#0f766e',
    author: 'EmergencyCard Health'
  }
};

// Comprehensive Dynamic SEO component
const updateSEOMeta = () => {
  const config = isStandaloneMode ? SEO_CONFIG.standalone : SEO_CONFIG.health;
  
  // Update document title
  document.title = config.title;
  
  // Helper to update or create meta tags
  const updateMeta = (name, content, isProperty = false) => {
    if (!content) return;
    const attr = isProperty ? 'property' : 'name';
    let meta = document.querySelector(`meta[${attr}="${name}"]`);
    if (meta) {
      meta.setAttribute('content', content);
    } else {
      meta = document.createElement('meta');
      meta.setAttribute(attr, name);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    }
  };
  
  // Helper to update or create link tags
  const updateLink = (rel, href, extra = {}) => {
    if (!href) return;
    let link = document.querySelector(`link[rel="${rel}"]`);
    if (link) {
      link.setAttribute('href', href);
    } else {
      link = document.createElement('link');
      link.setAttribute('rel', rel);
      link.setAttribute('href', href);
      Object.entries(extra).forEach(([k, v]) => link.setAttribute(k, v));
      document.head.appendChild(link);
    }
  };
  
  // Primary SEO Meta Tags
  updateMeta('title', config.title);
  updateMeta('description', config.description);
  updateMeta('keywords', config.keywords);
  updateMeta('author', config.author);
  updateMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
  updateMeta('theme-color', config.themeColor);
  
  // Open Graph Tags
  updateMeta('og:type', 'website', true);
  updateMeta('og:url', config.canonical + '/', true);
  updateMeta('og:title', config.title, true);
  updateMeta('og:description', config.description, true);
  updateMeta('og:image', config.ogImage, true);
  updateMeta('og:image:width', '1200', true);
  updateMeta('og:image:height', '630', true);
  updateMeta('og:site_name', config.siteName, true);
  updateMeta('og:locale', 'en_US', true);
  
  // Twitter Card Tags
  updateMeta('twitter:card', 'summary_large_image');
  updateMeta('twitter:site', config.twitterHandle);
  updateMeta('twitter:creator', config.twitterHandle);
  updateMeta('twitter:url', config.canonical + '/');
  updateMeta('twitter:title', config.title);
  updateMeta('twitter:description', config.description);
  updateMeta('twitter:image', config.ogImage);
  
  // Canonical URL - Critical for avoiding duplicate content
  updateLink('canonical', config.canonical);
  
  // Update existing canonical if present
  const existingCanonical = document.querySelector('link[rel="canonical"]');
  if (existingCanonical) {
    existingCanonical.setAttribute('href', config.canonical);
  }
  
  // Add structured data for the specific app
  const existingLD = document.querySelector('script[data-seo-ld]');
  if (existingLD) existingLD.remove();
  
  const ldScript = document.createElement('script');
  ldScript.type = 'application/ld+json';
  ldScript.setAttribute('data-seo-ld', 'true');
  
  if (isStandaloneMode) {
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "My EmergencyCard",
      "url": "https://myemergencycard.app",
      "description": config.description,
      "applicationCategory": "HealthApplication",
      "operatingSystem": "Any",
      "offers": {
        "@type": "Offer",
        "price": "1.00",
        "priceCurrency": "USD"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "ratingCount": "1250"
      }
    });
  } else {
    ldScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "EmergencyCard Health",
      "url": "https://emergencycard.health",
      "description": config.description,
      "applicationCategory": "HealthApplication",
      "operatingSystem": "Web, iOS, Android",
      "offers": [
        {"@type": "Offer", "name": "Monthly", "price": "2.99", "priceCurrency": "USD"},
        {"@type": "Offer", "name": "Annual", "price": "12.99", "priceCurrency": "USD"},
        {"@type": "Offer", "name": "Lifetime", "price": "49.99", "priceCurrency": "USD"}
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "2450"
      }
    });
  }
  document.head.appendChild(ldScript);
  
  console.log(`SEO initialized for: ${config.siteName} (${APP_MODE} mode)`);
};

// ============ STANDALONE APP IMPORTS (myemergencycard.app) ============
import EmergencyAppLanding from './pages/EmergencyAppLanding';
import ProductLandingPage from './pages/ProductLandingPage';
import CreateEmergencyCardPage from './pages/CreateEmergencyCardPage';
import EmergencyCardPrivacyPolicy from './pages/EmergencyCardPrivacyPolicy';
import EmergencyCardTerms from './pages/EmergencyCardTerms';
import EmergencyCardContact from './pages/EmergencyCardContact';
import EmergencyCardViewer from './pages/EmergencyCardViewer';
import HowItWorksPage from './pages/HowItWorksPage';
import WalkthroughSlideshow from './pages/WalkthroughSlideshow';

// ============ HEALTH APP IMPORTS (emergencycard.health) ============
import LoginPage from './pages/LoginPage';
import HealthProductLandingPage from './pages/HealthProductLandingPage';
import Dashboard from './pages/Dashboard';
import MedicalHistoryPage from './pages/MedicalHistoryPage';
import MedicalHistoryIntake from './pages/MedicalHistoryIntake';
import PrescriptionsPage from './pages/PrescriptionsPage';
import SupplementsPage from './pages/SupplementsPage';
import ReportsPage from './pages/ReportsPage';
import DiaryPage from './pages/DiaryPage';
import ReferralsPage from './pages/ReferralsPage';
import DoctorAccessPage from './pages/DoctorAccessPage';
import DoctorPortalPage from './pages/DoctorPortalPage';
import SettingsPage from './pages/SettingsPage';
import RemindersPage from './pages/RemindersPage';
import DownloadPDFPage from './pages/DownloadPDFPage';
import SharedPDFPage from './pages/SharedPDFPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import EmergencyProfilePage from './pages/EmergencyProfilePage';
import EmergencyViewPage from './pages/EmergencyViewPage';
import HealthAppPricing from './pages/HealthAppPricing';
import FamilyPage from './pages/FamilyPage';
import FamilyMemberDashboard from './pages/FamilyMemberDashboard';

import './App.css';

// Protected Route Component with Vault check
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const { vaultStatus, vaultSalt, vaultTestValue, setupVault, unlockVault } = useVault();
  
  if (loading || vaultStatus === 'loading') {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#0D2340] animate-pulse"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  // If user hasn't set up vault, show setup
  if (vaultStatus === 'setup') {
    return (
      <VaultPINSetup 
        userName={user?.name?.split(' ')[0]} 
        onComplete={async ({ salt, key, testValue }) => {
          await setupVault(salt, key, testValue);
        }}
      />
    );
  }
  
  // If vault is locked, show unlock
  if (vaultStatus === 'locked') {
    return (
      <VaultUnlock 
        salt={vaultSalt}
        testValue={vaultTestValue}
        userName={user?.name?.split(' ')[0]}
        onUnlock={unlockVault}
      />
    );
  }
  
  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#0D2340] animate-pulse"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  // ============================================================
  // STANDALONE MODE: myemergencycard.app
  // Simple $1 Emergency Card - No login required
  // ============================================================
  if (isStandaloneMode) {
    return (
      <Routes>
        {/* Product Landing page at root */}
        <Route path="/" element={<ProductLandingPage />} />
        
        {/* Original app landing (can be accessed via /app) */}
        <Route path="/app" element={<EmergencyAppLanding />} />
        
        {/* Core pages */}
        <Route path="/create" element={<CreateEmergencyCardPage />} />
        <Route path="/view" element={<EmergencyCardViewer />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/walkthrough" element={<WalkthroughSlideshow />} />
        
        {/* Legal pages */}
        <Route path="/privacy" element={<EmergencyCardPrivacyPolicy />} />
        <Route path="/terms" element={<EmergencyCardTerms />} />
        <Route path="/contact" element={<EmergencyCardContact />} />
        
        {/* Catch all - redirect to landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // ============================================================
  // HEALTH APP MODE: emergencycard.health
  // Paid Health Passport - Pay first, then login
  // ============================================================
  return (
    <Routes>
      {/* Product Landing page at root */}
      <Route path="/" element={<HealthProductLandingPage />} />
      
      {/* Pricing page (original landing) */}
      <Route path="/pricing" element={<HealthAppPricing />} />
      
      {/* Login page (after payment) */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      
      {/* Public routes */}
      <Route path="/doctor-portal" element={<DoctorPortalPage />} />
      <Route path="/shared-pdf/:shareCode" element={<SharedPDFPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/emergency/:publicId" element={<EmergencyViewPage />} />
      
      {/* Emergency Card routes (for cross-linking) */}
      <Route path="/emergency-app" element={<EmergencyAppLanding />} />
      <Route path="/emergency-app/create" element={<CreateEmergencyCardPage />} />
      <Route path="/emergency-app/view" element={<EmergencyCardViewer />} />
      <Route path="/emergency-app/privacy" element={<EmergencyCardPrivacyPolicy />} />
      <Route path="/emergency-app/terms" element={<EmergencyCardTerms />} />
      <Route path="/emergency-app/contact" element={<EmergencyCardContact />} />
      
      {/* Protected Routes - Require Login */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/family" 
        element={
          <ProtectedRoute>
            <FamilyPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/family/member" 
        element={
          <ProtectedRoute>
            <FamilyMemberDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/medical-history" 
        element={
          <ProtectedRoute>
            <MedicalHistoryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/medical-history/intake" 
        element={
          <ProtectedRoute>
            <MedicalHistoryIntake />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/prescriptions" 
        element={
          <ProtectedRoute>
            <PrescriptionsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/supplements" 
        element={
          <ProtectedRoute>
            <SupplementsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/diary" 
        element={
          <ProtectedRoute>
            <DiaryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/referrals" 
        element={
          <ProtectedRoute>
            <ReferralsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/doctor-access" 
        element={
          <ProtectedRoute>
            <DoctorAccessPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reminders" 
        element={
          <ProtectedRoute>
            <RemindersPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/download-pdf" 
        element={
          <ProtectedRoute>
            <DownloadPDFPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/emergency-profile" 
        element={
          <ProtectedRoute>
            <EmergencyProfilePage />
          </ProtectedRoute>
        } 
      />
      
      {/* Catch all - redirect to login */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Update SEO meta tags on mount
  useEffect(() => {
    updateSEOMeta();
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <VaultProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </VaultProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
